'use client';

import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useRouter } from 'next/navigation';
import {
  Search,
  Users,
  MessageCircle,
  UserPlus,
  UserCheck,
  Mic,
  Eye,
  SlidersHorizontal,
  ChevronDown,
} from 'lucide-react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { logger } from '@/lib/logger';
import {
  Avatar,
  Button as DsButton,
  Card,
  Input,
  Switch,
  Tag,
} from '@/components/ds';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/utils';

interface User {
  id: string;
  display_name: string;
  username: string;
  avatar_url: string;
  bio: string;
  account_types: string[];
  specialties?: string[];
  followers_count: number;
  following_count: number;
  is_following: boolean;
  is_self: boolean;
  // Structured talent fields (Phase 3).
  voice_age_min?: number | null;
  voice_age_max?: number | null;
  voice_type?: string[];
  union_status?: string[];
  open_to_unpaid?: boolean;
  min_rate_cents?: number | null;
  rate_unit?: string | null;
}

const VOICE_TYPE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['masculine', 'Masculine'],
  ['feminine', 'Feminine'],
  ['androgynous', 'Androgynous'],
  ['neutral', 'Neutral'],
];
const UNION_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['union', 'Union'],
  ['non-union', 'Non-union'],
];
const VOICE_TYPE_LABELS: Record<string, string> = {
  masculine: 'Masculine',
  feminine: 'Feminine',
  androgynous: 'Androgynous',
  neutral: 'Neutral',
};
const UNION_LABELS: Record<string, string> = {
  union: 'Union',
  'non-union': 'Non-union',
};
const RATE_UNIT_SUFFIX: Record<string, string> = {
  per_hour: '/hr',
  per_project: '/project',
  per_word: '/word',
  per_finished_minute: '/finished min',
};
function formatCastableAge(min: number | null, max: number | null): string | null {
  if (min != null && max != null) return `Plays ${min}–${max}`;
  if (min != null) return `Plays ${min}+`;
  if (max != null) return `Plays up to ${max}`;
  return null;
}
function formatMinRate(cents: number, unit: string | null): string {
  const dollars = cents / 100;
  const amount = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  return `${amount}${unit ? RATE_UNIT_SUFFIX[unit] ?? '' : ''}`;
}

export default function PeoplePage() {
  const router = useRouter();
  const { user } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'talent' | 'scout'>('all');

  // Structured talent filters (applied server-side via the search API).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [voiceTypeFilter, setVoiceTypeFilter] = useState<string[]>([]);
  const [unionFilter, setUnionFilter] = useState<string[]>([]);
  const [playsAge, setPlaysAge] = useState('');
  const [openToUnpaid, setOpenToUnpaid] = useState(false);
  const [maxRate, setMaxRate] = useState('');

  const activeFilterCount =
    (voiceTypeFilter.length > 0 ? 1 : 0) +
    (unionFilter.length > 0 ? 1 : 0) +
    (playsAge.trim() !== '' ? 1 : 0) +
    (openToUnpaid ? 1 : 0) +
    (maxRate.trim() !== '' ? 1 : 0);
  const hasTalentFilters = activeFilterCount > 0;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      params.set('limit', '50');
      if (voiceTypeFilter.length > 0) params.set('voice_type', voiceTypeFilter.join(','));
      if (unionFilter.length > 0) params.set('union_status', unionFilter.join(','));
      if (playsAge.trim() !== '') params.set('plays_age', playsAge.trim());
      if (openToUnpaid) params.set('open_to_unpaid', 'true');
      if (maxRate.trim() !== '') params.set('max_rate', maxRate.trim());
      const response = await fetch(`/api/users/search?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to search users');
      }
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error('[PeoplePage] Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, voiceTypeFilter, unionFilter, playsAge, openToUnpaid, maxRate]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleArrayFilter = (
    setter: Dispatch<SetStateAction<string[]>>,
    value: string
  ) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const clearTalentFilters = () => {
    setVoiceTypeFilter([]);
    setUnionFilter([]);
    setPlaysAge('');
    setOpenToUnpaid(false);
    setMaxRate('');
  };

  const clearAll = () => {
    setSearchQuery('');
    clearTalentFilters();
  };

  const chipClass = (on: boolean) =>
    cn(
      'h-8 px-3.5 rounded-sm text-sm font-medium transition-colors',
      on
        ? 'bg-accent text-text-on-accent'
        : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2 hover:text-text-primary'
    );

  const handleFollow = async (userId: string) => {
    if (!user) {
      router.push('/login');
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}/follow`, {
        method: 'POST',
      });

      if (response.ok) {
        const { is_following } = await response.json();
        setUsers(users.map((u) => (u.id === userId ? { ...u, is_following } : u)));
      }
    } catch (error) {
      logger.error('[PeoplePage] Error toggling follow:', error);
    }
  };

  // Route through the shared /messages?to=<id> deep-link, which opens-or-creates
  // the 1:1 thread via getOrCreateDirectConversation — the one working path.
  const startDM = (userId: string) => {
    router.push(`/messages?to=${userId}`);
  };

  const filteredUsers = users.filter((u) => {
    if (filter === 'all') return true;
    return u.account_types?.includes(filter);
  });

  const goToProfile = (u: User) => {
    if (u.username) {
      router.push(`/u/${u.username}`);
    } else {
      router.push(`/u/${u.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-surface-0 text-text-primary">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-text-primary mb-1 flex items-center gap-2">
              <Users className="w-6 h-6 text-accent" strokeWidth={1.75} />
              Discover People
            </h1>
            <p className="text-text-secondary text-sm">
              Connect with voice actors, scouts, and industry professionals
            </p>
          </div>

          {/* Search + Filter */}
          <Card className="mb-6 p-6">
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none"
                strokeWidth={1.75}
              />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or username..."
                className="pl-10"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={cn(
                  'h-8 px-3.5 rounded-sm text-sm font-medium transition-colors',
                  filter === 'all'
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2 hover:text-text-primary'
                )}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setFilter('talent')}
                className={cn(
                  'h-8 px-3.5 rounded-sm text-sm font-medium transition-colors inline-flex items-center gap-1.5',
                  filter === 'talent'
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2 hover:text-text-primary'
                )}
              >
                <Mic className="w-3.5 h-3.5" strokeWidth={1.75} />
                Talent
              </button>
              <button
                type="button"
                onClick={() => setFilter('scout')}
                className={cn(
                  'h-8 px-3.5 rounded-sm text-sm font-medium transition-colors inline-flex items-center gap-1.5',
                  filter === 'scout'
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2 hover:text-text-primary'
                )}
              >
                <Search className="w-3.5 h-3.5" strokeWidth={1.75} />
                Scouts
              </button>
            </div>

            {/* Structured talent filters (collapsible) */}
            <div className="mt-4 border-t border-border-subtle pt-4">
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  aria-expanded={filtersOpen}
                  className="inline-flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} />
                  Filters
                  {hasTalentFilters && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-text-on-accent text-[11px] font-semibold tabular-nums">
                      {activeFilterCount}
                    </span>
                  )}
                  <ChevronDown
                    className={cn('w-4 h-4 transition-transform', filtersOpen && 'rotate-180')}
                    strokeWidth={1.75}
                  />
                </button>
                {hasTalentFilters && (
                  <button
                    type="button"
                    onClick={clearTalentFilters}
                    className="text-xs text-text-secondary hover:text-text-primary underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {filtersOpen && (
                <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                  {/* Voice type */}
                  <div>
                    <span className="block text-xs font-medium text-text-secondary mb-2">Voice type</span>
                    <div className="flex flex-wrap gap-2">
                      {VOICE_TYPE_OPTIONS.map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          aria-pressed={voiceTypeFilter.includes(val)}
                          onClick={() => toggleArrayFilter(setVoiceTypeFilter, val)}
                          className={chipClass(voiceTypeFilter.includes(val))}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Union status */}
                  <div>
                    <span className="block text-xs font-medium text-text-secondary mb-2">Union status</span>
                    <div className="flex flex-wrap gap-2">
                      {UNION_OPTIONS.map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          aria-pressed={unionFilter.includes(val)}
                          onClick={() => toggleArrayFilter(setUnionFilter, val)}
                          className={chipClass(unionFilter.includes(val))}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Plays age */}
                  <div>
                    <label htmlFor="plays_age" className="block text-xs font-medium text-text-secondary mb-2">
                      Plays age
                    </label>
                    <Input
                      id="plays_age"
                      type="number"
                      min={0}
                      max={120}
                      inputMode="numeric"
                      placeholder="e.g. 30"
                      value={playsAge}
                      onChange={(e) => setPlaysAge(e.target.value)}
                      className="w-32"
                    />
                    <p className="text-text-tertiary text-[11px] mt-1">Actors who can voice this age.</p>
                  </div>

                  {/* Max rate */}
                  <div>
                    <label htmlFor="max_rate" className="block text-xs font-medium text-text-secondary mb-2">
                      Max rate (USD)
                    </label>
                    <Input
                      id="max_rate"
                      type="number"
                      min={0}
                      step="1"
                      inputMode="decimal"
                      placeholder="e.g. 100"
                      value={maxRate}
                      onChange={(e) => setMaxRate(e.target.value)}
                      className="w-32"
                    />
                    <p className="text-text-tertiary text-[11px] mt-1">Filters on the actor&apos;s stated minimum.</p>
                  </div>

                  {/* Open to unpaid */}
                  <div className="flex items-center gap-3 sm:col-span-2">
                    <Switch
                      checked={openToUnpaid}
                      onCheckedChange={setOpenToUnpaid}
                      aria-label="Open to unpaid work"
                    />
                    <span className="text-sm text-text-secondary">Open to unpaid work</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Result count */}
          {!loading && (
            <p className="text-text-secondary text-sm mb-4 tabular-nums">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'result' : 'results'}
            </p>
          )}

          {/* User Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-10 w-10 border-b-2 border-accent rounded-full" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              size="lg"
              icon={Users}
              title="No matches found"
              description="Try a different search term, or browse all members to find people you want to connect with."
              action={
                searchQuery || hasTalentFilters ? (
                  <DsButton variant="secondary" size="md" onClick={clearAll}>
                    Clear search &amp; filters
                  </DsButton>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map((u) => (
                <Card key={u.id} hoverable className="cursor-pointer" onClick={() => goToProfile(u)}>
                  {/* Header row: avatar + role tags */}
                  <div className="flex items-start justify-between mb-3">
                    <Avatar
                      src={u.avatar_url || undefined}
                      name={u.display_name || u.username || 'User'}
                      size={56}
                    />
                    <div className="flex gap-1 flex-wrap justify-end">
                      {u.account_types?.includes('talent') && (
                        <Tag>
                          <Mic className="w-3 h-3" strokeWidth={1.75} />
                          Talent
                        </Tag>
                      )}
                      {u.account_types?.includes('scout') && <Tag>Scout</Tag>}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="mb-2">
                    <h3 className="text-text-primary text-sm font-semibold truncate">
                      {u.display_name || 'Unknown User'}
                    </h3>
                    <p className="text-text-secondary text-xs">
                      @
                      {u.username ||
                        u.display_name?.toLowerCase().replace(/\s/g, '') ||
                        'unknown'}
                    </p>
                  </div>

                  {/* Bio */}
                  {u.bio && (
                    <p className="text-text-secondary text-xs line-clamp-2 mb-3">{u.bio}</p>
                  )}

                  {/* Specialties */}
                  {u.specialties && u.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {u.specialties.slice(0, 4).map((s) => (
                        <Tag key={s}>{s}</Tag>
                      ))}
                    </div>
                  )}

                  {/* Talent details — why they matched. Hidden when all blank. */}
                  {(() => {
                    const ageLabel = formatCastableAge(
                      u.voice_age_min ?? null,
                      u.voice_age_max ?? null
                    );
                    const vTypes = Array.isArray(u.voice_type) ? u.voice_type : [];
                    const unions = Array.isArray(u.union_status) ? u.union_status : [];
                    const rate =
                      typeof u.min_rate_cents === 'number'
                        ? formatMinRate(u.min_rate_cents, u.rate_unit ?? null)
                        : null;
                    const unpaid = !!u.open_to_unpaid;
                    if (!ageLabel && vTypes.length === 0 && unions.length === 0 && !rate && !unpaid) {
                      return null;
                    }
                    return (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {ageLabel && <Tag>{ageLabel}</Tag>}
                        {vTypes.map((v) => (
                          <Tag key={`vt-${v}`}>{VOICE_TYPE_LABELS[v] ?? v}</Tag>
                        ))}
                        {unions.map((v) => (
                          <Tag key={`un-${v}`}>{UNION_LABELS[v] ?? v}</Tag>
                        ))}
                        {rate && <Tag>{rate}</Tag>}
                        {unpaid && <Tag>Open to unpaid</Tag>}
                      </div>
                    );
                  })()}

                  {/* Stats */}
                  <div className="flex gap-3 text-xs text-text-secondary mb-3">
                    <span>
                      <strong className="text-text-primary tabular-nums">
                        {u.followers_count || 0}
                      </strong>{' '}
                      followers
                    </span>
                    <span>
                      <strong className="text-text-primary tabular-nums">
                        {u.following_count || 0}
                      </strong>{' '}
                      following
                    </span>
                  </div>

                  {/* Actions */}
                  {!u.is_self && (
                    <div
                      className="flex gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DsButton
                        variant="secondary"
                        size="sm"
                        onClick={() => goToProfile(u)}
                        className="flex-1 justify-center"
                      >
                        <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                        View Profile
                      </DsButton>
                      <DsButton
                        variant={u.is_following ? 'secondary' : 'primary'}
                        size="sm"
                        onClick={() => handleFollow(u.id)}
                      >
                        {u.is_following ? (
                          <>
                            <UserCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Following
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Follow
                          </>
                        )}
                      </DsButton>
                      <DsButton
                        variant="ghost"
                        size="sm"
                        onClick={() => startDM(u.id)}
                        aria-label="Send message"
                      >
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </DsButton>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
  );
}
